import { useGetAllinstanceForAUser } from "@/api/instance.ts";
import InventoZen from "@/components/icons/InventoZen.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { fetchMainLanguage } from "@/i18n";
import { cn } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Instance = () => {
  const { t } = useTranslation();
  const { user, setInstance: setInstanceGlobal } = useAuth();
  const [instance, setInstance] = useState<string>("");
  const navigation = useNavigate();

  const { data: instancesRaw } = useGetAllinstanceForAUser(user?.id);
  const instances = useMemo(
    () =>
      instancesRaw
        ? [...instancesRaw].sort((a, b) =>
            a.instance.name.localeCompare(b.instance.name),
          )
        : [],
    [instancesRaw],
  );

  const backgroundImageUrl = t("loginBackgroundImageUrl", { defaultValue: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (instance) {
      localStorage.setItem("instance", instance);
      setInstanceGlobal(instance);
      fetchMainLanguage();
      navigation("/dashboard");
    }
  };

  return (
    <div className="w-full h-full justify-between items-center inline-flex">
      <div className="w-full h-screen relative">
        <div className="h-full flex items-center justify-center">
          <div className="m-auto w-[409px] h-fit p-10 bg-white rounded-lg shadow border border-slate-200 flex-col justify-start items-center gap-8 inline-flex">
            <div>
              <div className="justify-center items-center flex">
                <InventoZen />
              </div>
              <div className="text-center text-slate-900 text-3xl font-semibold font-['Instrument Sans'] leading-loose">
                {t("instanceChoice")}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="w-full grid">
              <Label
                className="text-start font-['Raleway'] mb-1.5"
                htmlFor="instance"
              >
                {t("instance")}
              </Label>
              <Select
                onValueChange={(e) => setInstance(e)}
                defaultValue={instance}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectAnInstance")} />
                </SelectTrigger>
                <SelectContent>
                  {instances?.map(
                    (
                      elt: { instance: { name: string }; role: string },
                      index: number,
                    ) => (
                      <SelectItem
                        className="cursor-pointer flex justify-between flex-row"
                        key={elt.instance.name + "_" + index}
                        value={elt.instance.name}
                      >
                        <span>{elt.instance.name}</span>
                        <span> ({elt.role})</span>
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <Button
                type="submit"
                variant="outline"
                className="mt-8 h-12 px-4 py-3 bg-[#008bd4] rounded-lg border border-[#006eab] justify-center items-center gap-1.5 inline-flex"
              >
                <div className="px-0.5 justify-center items-center flex">
                  <p className="text-white text-base font-semibold font-['Raleway'] leading-normal">
                    {t("continue")}
                  </p>
                </div>
              </Button>
            </form>
          </div>
        </div>
        <div className="absolute bottom-0 w-full h-16 px-12 py-5 bg-white border-t border-slate-200 flex justify-between items-center">
          <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
            {`${t("copyrightInventoZen")} | ${t("version")}: ${import.meta.env.VITE_CURRENT_GIT_SHA}`}
          </div>
          <div className="justify-start items-start gap-5 flex">
            <a
              href={"#"}
              className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal"
            >
              {t("CGV")}
            </a>
            <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
              |
            </div>
            <a
              href={"#"}
              className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal"
            >
              {t("footerLegalNotices")}
            </a>
            <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
              |
            </div>
            <p className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal">
              {t("optimizeForChrome")}
            </p>
          </div>
        </div>
      </div>
      <div
        className={cn(
          "h-screen relative md:hidden lg:block transition-all duration-300",
          backgroundImageUrl ? "w-[50%]" : "w-0",
        )}
      >
        {backgroundImageUrl && (
          <img
            src={backgroundImageUrl}
            alt="login"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute bottom-0 left-0 w-full flex-col justify-center items-center inline-flex">
          <div className="self-stretch p-8 bg-white/10 border-t border-white/10 backdrop-blur-xl flex-col justify-start items-start gap-8 flex">
            <div className="h-14 flex-col justify-start items-start gap-1 flex">
              <p className="grow shrink basis-0 text-white text-xl font-semibold font-['Instrument Sans'] leading-normal text-left">
                {t("titleOfTheWork")}
              </p>
              <p className="self-stretch text-white text-lg font-normal font-['Raleway'] leading-normal text-left">
                {t("artistFirstPage")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instance;
